import Big from "big.js";

import {
    AbsoluteModelPath,
    convertToAbsoluteModelPath,
    createFromMask,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { isNotNullOrEmpty, isNotNullOrUndefined, isNullOrEmpty, reject } from "../../../../../Common/TypingUtils";
import { wrapDecimalOperand } from "../../../Helpers/AutocalcCommonFunctions";
import { isAggregationPath } from "../../../../../Common/ModelPath/ModelPathHelper";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { ValidationHelper } from "../../ValidationHelper/ValidationHelper";
import {
    addDays,
    addMonths,
    addYears,
    convertDate,
    dateToString,
    differenceInDays,
    differenceInMonths,
    getLastDateOfMonth,
    isDate,
} from "../../../Helpers/DateHelpers";
import {
    DigestFilterOperator,
    DigestLogicalOperator,
} from "../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";

import { IEvaluationContext } from "./IEvaluationContext";

export type PlainModelValue = string | undefined;

export interface IKCExpressionRuntime {
    when(
        condition: boolean | undefined,
        body: () => boolean | undefined | Promise<boolean | undefined>
    ): boolean | undefined | Promise<boolean | undefined>;
    or(conditions: Array<boolean | undefined>): boolean | undefined;
    and(conditions: Array<boolean | undefined>): boolean | undefined;
    not(condition: boolean | undefined): boolean | undefined;
    eq(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined;
    gt(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined;
    lt(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined;
    ge(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined;
    le(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined;
    const(value: PlainModelValue): PlainModelValue;
    argument(modelPath: string | AbsoluteModelPath): PlainModelValue;
    count(modelPath: string | AbsoluteModelPath, withNullOrEmptyValues?: boolean): PlainModelValue;
    instanceCount(modelPath: string | AbsoluteModelPath, withNullOrEmptyValues?: boolean): PlainModelValue;
    exists(modelPath: string | AbsoluteModelPath): boolean;
    isValueEqlAutoValue(modelPath: string | AbsoluteModelPath): boolean | undefined;
    isSnils(value: PlainModelValue): boolean;
    isRegNumSfr(value: PlainModelValue): boolean;
    isInn(value: PlainModelValue): boolean;
    isOgrn(value: PlainModelValue): boolean;
    isValidMirCardNumber(value: PlainModelValue): boolean;
    isValidAccountNumber(bikValue: PlainModelValue, accountValue: PlainModelValue): boolean;
    length(value: PlainModelValue): PlainModelValue;
    regexMatch(value: PlainModelValue, pattern: string): boolean;
    regexReplace(value: PlainModelValue, pattern: string, replacement: string, flags?: string): string;
    multySum(modelPath: string | AbsoluteModelPath): PlainModelValue;
    groupSum(targetKeys: PlainModelValue | undefined, sourceKeyPath: string, sourceValuePath: string): PlainModelValue;
    groupCount(targetKeys: PlainModelValue | undefined, sourceKeyPath: string): PlainModelValue;
    multiply(values: PlainModelValue[]): PlainModelValue;
    division(values: PlainModelValue[]): PlainModelValue;
    sum(values: PlainModelValue[]): PlainModelValue;
    minus(value: PlainModelValue): PlainModelValue;
    abs(value: PlainModelValue): PlainModelValue;
    xabs(value: PlainModelValue): PlainModelValue;
    choose(
        condition: boolean | undefined,
        thenBlock: () => PlainModelValue,
        elseBlock: () => PlainModelValue
    ): PlainModelValue;
    substring(modelPath: PlainModelValue, start: number, length: number): PlainModelValue;
    concat(values: PlainModelValue[]): PlainModelValue;
    dateNow(): Date;
    getDaysInMonth(value: PlainModelValue): PlainModelValue;
    getDay(value: PlainModelValue): PlainModelValue;
    getMonth(value: PlainModelValue): PlainModelValue;
    getYear(value: PlainModelValue): PlainModelValue;
    isDate(value: PlainModelValue): boolean | undefined;
    differenceInMonths(values: PlainModelValue[]): PlainModelValue;
    differenceInDays(values: PlainModelValue[]): PlainModelValue;
    addDays(value: PlainModelValue, amount: number | PlainModelValue): Date | undefined;
    addMonths(value: PlainModelValue, amount: number | PlainModelValue): Date | undefined;
    addYears(value: PlainModelValue, amount: number | PlainModelValue): Date | undefined;
    dateToString(value: Date | undefined, format: string): PlainModelValue;
    dateTime(value: PlainModelValue): Date | undefined;
    oneOf(value: PlainModelValue, items: PlainModelValue[]): boolean;
    join(values: PlainModelValue[]): string;
    getProperty(name: string, getDefaultValue: () => string | undefined): string | undefined;
    getPicklistValues(
        gid: string,
        filterColumns: string[],
        filterValues: PlainModelValue[],
        resultColumnName: string
    ): Promise<string[]>;
    round(value: PlainModelValue, fractional?: number | PlainModelValue): PlainModelValue;
    floor(value: PlainModelValue): PlainModelValue;
    numerator(value: PlainModelValue): PlainModelValue;
    denominator(value: PlainModelValue): PlainModelValue;
    uuid(): string;
}

export class KCExpressionRuntime implements IKCExpressionRuntime {
    private readonly context: IEvaluationContext;

    public constructor(context: IEvaluationContext) {
        this.context = context;
    }

    public join(values: PlainModelValue[]): string {
        return values.join("");
    }

    public getProperty(name: string, getDefaultValue: () => string | undefined): string | undefined {
        const settingValue = this.context.getExternalPropertiesProvider().getExternalProperty(`@settings/${name}`);
        if (settingValue != null && settingValue != "") {
            return settingValue.toString();
        }
        const featureFlag = this.context.getExternalPropertiesProvider().getExternalProperty(`@featureFlags/${name}`);
        if (featureFlag != null && featureFlag != "") {
            return featureFlag.toString();
        }
        const requisiteProperty = this.context.getExternalPropertiesProvider().getExternalProperty(`@${name}`);
        if (requisiteProperty != null) {
            return requisiteProperty.toString();
        }
        return getDefaultValue != undefined ? getDefaultValue() : "";
    }

    public async getPicklistValues(
        gid: string,
        filterColumns: string[],
        filterValues: PlainModelValue[],
        resultColumnName: string
    ): Promise<string[]> {
        // NOTE В общем, за каким-то хером при компиляции массива его делают JSON.stringify, я подозреваю, зачем это, но
        // сейчас не хочу с этим разбираться, на тот случай, если это исправят тут так, через if
        if (typeof filterColumns === "string") {
            filterColumns = JSON.parse(filterColumns);
        }
        if (typeof filterValues === "string") {
            filterValues = JSON.parse(filterValues);
        }
        const queryResult =
            (await this.context
                .getPicklistProvider()
                .getPicklist({ gId: gid })
                ?.query({
                    filter: {
                        operator: DigestLogicalOperator.And,
                        operands: filterColumns.map((column, index) => ({
                            operator: DigestFilterOperator.Equal,
                            column: column,
                            value: filterValues[index]?.toString() ?? "",
                        })),
                    },
                })) ?? reject("Failed to query picklist");
        return queryResult.items.map(x => x[resultColumnName]?.toString() ?? "");
    }

    public argument(modelPath: string): string | undefined {
        return this.getSingleValueOrSumByAllInstances(modelPath);
    }

    public count(modelPath: string | AbsoluteModelPath, withNullOrEmptyValues?: boolean): string | undefined {
        return this.getCountByAllInstances(convertToAbsoluteModelPath(modelPath), withNullOrEmptyValues);
    }

    public instanceCount(modelPath: string | AbsoluteModelPath): string | undefined {
        return this.context
            .getInstanceCount(this.context.resolvePath(convertToAbsoluteModelPath(modelPath)))
            .toString();
    }

    public const(value: PlainModelValue): PlainModelValue {
        return value;
    }

    public eq(left: PlainModelValue | boolean | Date, right: PlainModelValue | boolean | Date): boolean | undefined {
        if (left instanceof Date || right instanceof Date) {
            if (!(left instanceof Date && right instanceof Date)) {
                return undefined;
            }
            return left.getTime() === right.getTime();
        }
        if (typeof left === "boolean" && typeof right === "string") {
            // Эта пара странных проверок нужна для выражений вида
            // if CheckBoxValue as string == 'true' ...
            // Иногда значением CheckBoxValue может быть true (bool, это по умолчанию, если чекбокс не настроить),
            // а иногда 'true' -- строка, если настроить. Так вот, компилятор не хочет этот cast к строке писать, считая что там должны быть строки
            // NOTE Я буду гореть в аду за это, но теперь война за косистентность не моя
            return left.toString() === right;
        }
        if (typeof left === "string" && typeof right === "boolean") {
            // NOTE Я буду гореть в аду за это, но теперь война за косистентность не моя
            return left === right.toString();
        }

        return left === right;
    }

    public gt(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined {
        if (left instanceof Date || right instanceof Date) {
            if (!(left instanceof Date && right instanceof Date)) {
                return undefined;
            }
            return left > right;
        }

        return wrapDecimalOperand(left).gt(wrapDecimalOperand(right));
    }

    public lt(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined {
        if (left instanceof Date || right instanceof Date) {
            if (!(left instanceof Date && right instanceof Date)) {
                return undefined;
            }
            return left < right;
        }

        return wrapDecimalOperand(left).lt(wrapDecimalOperand(right));
    }

    public ge(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined {
        if (left instanceof Date || right instanceof Date) {
            if (!(left instanceof Date && right instanceof Date)) {
                return undefined;
            }
            return left >= right;
        }

        return wrapDecimalOperand(left).gte(wrapDecimalOperand(right));
    }

    public le(left: PlainModelValue | Date, right: PlainModelValue | Date): boolean | undefined {
        if (left instanceof Date || right instanceof Date) {
            if (!(left instanceof Date && right instanceof Date)) {
                return undefined;
            }
            return left <= right;
        }

        return wrapDecimalOperand(left).lte(wrapDecimalOperand(right));
    }

    public when(
        condition: boolean | undefined,
        body: () => boolean | undefined | Promise<boolean | undefined>
    ): boolean | undefined | Promise<boolean | undefined> {
        if (condition) {
            const b = body();
            return b;
        }
        return true;
    }

    public or(conditions: Array<boolean | undefined>): boolean | undefined {
        return conditions.reduce(
            (acc, current) => (acc == undefined || current == undefined ? undefined : acc || current),
            false
        );
    }

    public and(conditions: Array<boolean | undefined>): boolean | undefined {
        return conditions.reduce(
            (acc, current) => (acc == undefined || current == undefined ? undefined : acc && current),
            true
        );
    }

    public not(condition: boolean | undefined): boolean | undefined {
        return !condition;
    }

    public exists(path: string | AbsoluteModelPath): boolean {
        return this.checkExistsInAllInstances(convertToAbsoluteModelPath(path));
    }

    public isValueEqlAutoValue(path: string | AbsoluteModelPath): boolean | undefined {
        const value = this.context.get(convertToAbsoluteModelPath(path));
        const autoValue = this.context.getAutoValue(convertToAbsoluteModelPath(path));
        return this.eq(value, autoValue);
    }

    public isSnils(value: PlainModelValue): boolean {
        const normalizedSnils = value?.replace(/[^0-9]/g, "");
        if (isNullOrEmpty(normalizedSnils)) {
            return false;
        }
        const result = ValidationHelper.isSnils(normalizedSnils);

        return result;
    }

    public isInn(value: PlainModelValue): boolean {
        const result = ValidationHelper.isInn(value);
        return result;
    }

    public isOgrn(value: PlainModelValue): boolean {
        const result = ValidationHelper.isOgrn(value);
        return result;
    }

    public isRegNumSfr(value: PlainModelValue): boolean {
        const normalizedRegNum = value?.replace(/[^0-9]/g, "");
        if (isNullOrEmpty(normalizedRegNum)) {
            return false;
        }
        const result = ValidationHelper.isRegNumSfr(normalizedRegNum);
        return result;
    }

    public isValidAccountNumber(bikValue: PlainModelValue, accountValue: PlainModelValue): boolean {
        if (isNullOrEmpty(bikValue) || isNullOrEmpty(accountValue)) {
            return false;
        }
        const result = ValidationHelper.isValidAccountNumber(bikValue, accountValue);
        return result;
    }

    public isValidMirCardNumber(value: PlainModelValue): boolean {
        const normalizedCard = value?.replace(/[^0-9]/g, "");
        if (isNullOrEmpty(normalizedCard)) {
            return false;
        }
        const result = ValidationHelper.isValidMirCardNumber(normalizedCard);
        return result;
    }

    public length(value: PlainModelValue): PlainModelValue {
        return isNotNullOrUndefined(value) ? value.length.toString() : "0";
    }

    public regexMatch(value: PlainModelValue, pattern: string): boolean {
        return value != undefined && new RegExp(pattern).test(value);
    }

    public regexReplace(value: PlainModelValue, pattern: string, replacement: string, flags?: string): string {
        if (value == undefined) {
            return "";
        }

        return value.replace(new RegExp(pattern, flags), replacement);
    }

    public multySum(modelPath: string | AbsoluteModelPath): PlainModelValue {
        return this.getSingleValueOrSumByAllInstances(modelPath);
    }

    private unpackPlainModelValue(
        source: PlainModelValue | undefined
    ): PlainModelValue[] | PlainModelValue | undefined {
        if (source == undefined) {
            return source;
        }

        if (IterUtils.first(source) === "[" && IterUtils.last(source) === "]") {
            return JSON.parse(source) as PlainModelValue[];
        }

        return source;
    }

    private unpackGroupKeys(source: PlainModelValue | undefined): PlainModelValue[] {
        const targetKeys = this.unpackPlainModelValue(source);
        const groupKeys = (Array.isArray(targetKeys) ? targetKeys : [targetKeys])
            .map(x => {
                const value = this.unpackPlainModelValue(x?.toString());
                return Array.isArray(value) ? value : [value];
            })
            .flat()
            .map(x => (x != undefined ? `${x}` : undefined));
        return groupKeys;
    }

    public groupCount(targetKeysSource: PlainModelValue | undefined, sourceKeyPath: string): PlainModelValue {
        const groupKeys = new Set(this.unpackGroupKeys(targetKeysSource));

        const sourceKeyUnresolvedPath = createFromMask(sourceKeyPath, true, PathTokens.each);
        const contextForSource = this.context.resolvePath(sourceKeyUnresolvedPath).isContainIteration()
            ? this.context
            : this.context.popIterationContext();

        const sourceKeyModelPath = contextForSource.resolvePath(sourceKeyUnresolvedPath);

        const count = Iterator.from(IterUtils.iterateEntries(contextForSource.getAllResolvedValues(sourceKeyModelPath)))
            .filter(([_, key]) => groupKeys.has(key))
            .reduce((acc, _) => acc.add(1), wrapDecimalOperand(0));
        return count?.toFixed();
    }

    public groupSum(
        targetKeysSource: PlainModelValue | undefined,
        sourceKeyPath: string,
        sourceValuePath: string
    ): PlainModelValue {
        const groupKeys = new Set(this.unpackGroupKeys(targetKeysSource));

        const sourceKeyUnresolvedPath = createFromMask(sourceKeyPath, true, PathTokens.each);
        const sourceValueUnresolvedPath = createFromMask(sourceValuePath, true, PathTokens.each);
        const contextForSource = this.context.resolvePath(sourceKeyUnresolvedPath).isContainIteration()
            ? this.context
            : this.context.popIterationContext();

        const sourceKeyModelPath = contextForSource.resolvePath(sourceKeyUnresolvedPath);
        const sourceValueModelPath = contextForSource.resolvePath(sourceValueUnresolvedPath);

        const keysMatch: Record<number, boolean> = {};
        for (const [index, key] of IterUtils.iterateEntries(
            contextForSource.getAllResolvedValues(sourceKeyModelPath)
        )) {
            keysMatch[index] = groupKeys.has(key);
        }

        return IterUtils.reduce<string | undefined, Big | undefined>(
            contextForSource.getAllResolvedValues(sourceValueModelPath),
            (prev, value, i) => {
                if (!keysMatch[i]) {
                    return prev;
                }
                return wrapDecimalOperand(prev).add(wrapDecimalOperand(value));
            },
            undefined
        )?.toFixed();
    }

    public multiply(values: PlainModelValue[]): PlainModelValue {
        const result = values.map(x => wrapDecimalOperand(x)).reduce((prev, value) => prev.mul(value), Big(1));
        return result?.toFixed();
    }

    public division(values: PlainModelValue[]): PlainModelValue {
        const first = values[0];
        const rest = values.slice(1);
        const result = rest
            .map(x => (x == undefined || x === "" ? undefined : wrapDecimalOperand(x)))
            .reduce<Big | undefined>(
                (prev, value) => {
                    if (prev == undefined || value == undefined || value.eq(0)) {
                        return undefined;
                    }
                    return prev?.div(value);
                },
                first != undefined ? wrapDecimalOperand(first) : wrapDecimalOperand(0)
            );
        return result?.toFixed();
    }

    public sum(values: PlainModelValue[]): PlainModelValue {
        const result = values.reduce<Big | undefined>(
            (prev, value) =>
                (value === "" || value == undefined) && prev == undefined
                    ? undefined
                    : wrapDecimalOperand(value).add(wrapDecimalOperand(prev)),
            undefined
        );
        return result?.toFixed();
    }

    public minus(value: PlainModelValue): PlainModelValue {
        return isNotNullOrEmpty(value) ? Big(0).minus(wrapDecimalOperand(value)).toFixed() : undefined;
    }

    public abs(value: PlainModelValue): PlainModelValue {
        return isNotNullOrEmpty(value) ? wrapDecimalOperand(value).abs().toFixed() : undefined;
    }

    public xabs(value: PlainModelValue): PlainModelValue {
        if (isNullOrEmpty(value)) {
            return undefined;
        }
        const wrappedValue = wrapDecimalOperand(value);
        return wrappedValue.lt(0) ? "0" : wrappedValue.abs().toFixed();
    }

    public round(value: PlainModelValue, fractional?: number | PlainModelValue): PlainModelValue {
        const fractionalValue = typeof fractional === "string" ? Number(fractional) : fractional;
        return isNotNullOrEmpty(value) ? wrapDecimalOperand(value).round(fractionalValue).toFixed() : undefined;
    }

    public floor(value: PlainModelValue): PlainModelValue {
        return isNotNullOrEmpty(value) ? wrapDecimalOperand(value).round(undefined, 0).toFixed() : undefined;
    }

    public choose(
        condition: boolean | undefined,
        thenBlock: () => PlainModelValue,
        elseBlock: () => PlainModelValue
    ): PlainModelValue {
        return condition != undefined ? (condition ? thenBlock() : elseBlock()) : undefined;
    }

    public dateNow(): Date {
        return new Date();
    }

    public getDaysInMonth(value: PlainModelValue): PlainModelValue {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return getLastDateOfMonth(date).toString();
    }

    public getDay(value: PlainModelValue): PlainModelValue {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return date.getDate().toString();
    }

    public getMonth(value: PlainModelValue): PlainModelValue {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return (date.getMonth() + 1).toString();
    }

    public getYear(value: PlainModelValue): PlainModelValue {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return date.getFullYear().toString();
    }

    public isDate(value: PlainModelValue): boolean | undefined {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        return isDate(date);
    }

    public differenceInMonths(values: PlainModelValue[]): PlainModelValue {
        const leftDate = values[0] ? convertDate(values[0]) : undefined;
        const rightDate = values[1] ? convertDate(values[1]) : undefined;

        if (!leftDate || !rightDate) {
            return undefined;
        }

        return differenceInMonths(leftDate, rightDate).toString();
    }

    public differenceInDays(values: PlainModelValue[]): PlainModelValue {
        const leftDate = values[0] ? convertDate(values[0]) : undefined;
        const rightDate = values[1] ? convertDate(values[1]) : undefined;

        if (!leftDate || !rightDate) {
            return undefined;
        }

        return differenceInDays(leftDate, rightDate).toString();
    }

    public addDays(value: PlainModelValue, amount: number | string): Date | undefined {
        const amountValue = typeof amount === "string" ? Number(amount) : amount;
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return addDays(date, amountValue);
    }

    public addMonths(value: PlainModelValue, amount: number | string): Date | undefined {
        const amountValue = typeof amount === "string" ? Number(amount) : amount;
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return addMonths(date, amountValue);
    }

    public addYears(value: PlainModelValue, amount: number | string): Date | undefined {
        const amountValue = typeof amount === "string" ? Number(amount) : amount;
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return addYears(date, amountValue);
    }

    public dateToString(date: Date | undefined, format: string): PlainModelValue {
        let dateFormat = format;

        if (format === "fss") {
            dateFormat = "yyyy-MM-dd";
        }

        return date != undefined ? dateToString(date, dateFormat) : undefined;
    }

    public dateTime(value: PlainModelValue): Date | undefined {
        const date = isNotNullOrEmpty(value) ? convertDate(value) : undefined;
        if (!date) {
            return undefined;
        }

        return date;
    }

    public substring(value: PlainModelValue | Date, start: number, length: number): PlainModelValue {
        if (value instanceof Date) {
            return value.toLocaleDateString("ru-RU").substr(start, length);
        }

        return value?.substr(start, length);
    }

    public concat(values: PlainModelValue[]): PlainModelValue {
        const result = values.join("");
        return result;
    }

    public oneOf(value: PlainModelValue, items: PlainModelValue[]): boolean {
        return items.some(x => x === value);
    }

    private getSingleValueOrSumByAllInstances(path: string | AbsoluteModelPath): string | undefined {
        const modelPath = this.context.resolvePath(convertToAbsoluteModelPath(path));
        if (modelPath.isResolved()) {
            return this.joinIfArray(this.context.get(modelPath));
        }
        const finalValue = modelPath.isContainIteration()
            ? Iterator.from(this.context.getAllResolvedValues(modelPath))
                  .reduce<Big | undefined>(
                      (prev, value) =>
                          isNotNullOrEmpty(value) ? wrapDecimalOperand(value).add(wrapDecimalOperand(prev)) : prev,
                      undefined
                  )
                  ?.toFixed()
            : this.context.get(modelPath);
        return finalValue;
    }

    private joinIfArray(value: string | undefined): string | undefined {
        if (value == undefined) {
            return value;
        }
        try {
            const deserializedValue = value.startsWith("[") ? JSON.parse(value ?? reject()) : value;
            return deserializedValue instanceof Array ? deserializedValue.join(", ") : value;
        } catch {
            return value;
        }
    }

    private getCountByAllInstances(path: AbsoluteModelPath, withNullOrEmptyValues?: boolean): string | undefined {
        const modelPath = this.context.resolvePath(path);
        // if (IterUtils.last(modelPath.getPathParts()) == PathTokens.each) {
        //     let number = IterUtils.count(this.context.getInstances(modelPath.getParentPath()));
        //     return number.toString();
        // }
        const finalValue = modelPath.isContainIteration()
            ? Iterator.from(this.context.getAllResolvedValues(modelPath))
                  .reduce<Big | undefined>(
                      (prev, value) =>
                          withNullOrEmptyValues === true || isNotNullOrEmpty(value)
                              ? wrapDecimalOperand(1).add(wrapDecimalOperand(prev))
                              : prev,
                      undefined
                  )
                  ?.toFixed()
            : isAggregationPath(modelPath)
              ? this.context.get(modelPath)
              : isNotNullOrEmpty(this.context.get(modelPath))
                ? "1"
                : "0";
        return finalValue;
    }

    private checkExistsInAllInstances(modelPath: AbsoluteModelPath): boolean {
        const resolvedModelPath = this.context.resolvePath(modelPath);
        const isMultiplicityPath = resolvedModelPath.isContainIteration();
        return Iterator.from(this.context.getAllResolvedValues(modelPath)).some(
            x => isMultiplicityPath || !isNullOrEmpty(x)
        );
    }

    public numerator(value: PlainModelValue): PlainModelValue {
        if (isNullOrEmpty(value)) {
            return "0";
        }

        const parts = value?.split("/");

        return wrapDecimalOperand(parts[0])?.toFixed();
    }

    public denominator(value: PlainModelValue): PlainModelValue {
        if (isNullOrEmpty(value)) {
            return "0";
        }

        const parts = value?.split("/");

        if (isNullOrEmpty(parts[1])) {
            return "1";
        }

        return wrapDecimalOperand(parts[1])?.toFixed();
    }

    public uuid(): string {
        return self.crypto.randomUUID();
    }
}
